import { forwardRef } from "react";
import {
  bool,
  element,
  elementType,
  func,
  number,
  node,
  object,
  oneOfType,
  string,
} from "prop-types";
import styled, { css } from "styled-components/macro";
import { darken, lighten, rem } from "polished";

import { colors, fonts } from "../Styles.variables";

const getTextColor = ({ borderless, color, outline, textColor }, state) => {
  if (!!textColor && !!colors[textColor]) {
    return colors[textColor];
  }

  if (borderless && state === "hover") {
    return lighten(0.2, colors[color]);
  }

  if (borderless && state === "disabled") {
    return lighten(0.4, colors[color]);
  }

  if ((borderless || outline) && state !== "hover") {
    return colors[color];
  }

  if (["gray100", "gray150", "gray200", "white", "yellow"].includes(color)) {
    return colors.black;
  }

  return colors.white;
};

const baseStyles = css`
  appearance: none;
  font-weight: ${fonts.weights.bold};
  line-height: 1;
  outline: none;
  text-align: center;
  text-decoration: none !important;
  text-transform: ${({ lower }) => (lower ? "none" : "uppercase")};
  transition-duration: 0.15s;
  transition-property: background-color, border-color, color;
  transition-timing-function: ease-in-out;
  user-select: none;
  vertical-align: middle;
`;

const compactStyles = css`
  border-radius: ${props => getBorderRadius(props, "3px")};
  border-width: ${rem("1px")};
  font-size: ${rem("11px")};
  padding: ${rem("6px")} ${rem("8px")};
`;

const compactSmallStyles = css`
  border-radius: ${props => getBorderRadius(props, "3px")};
  border-width: ${rem("2px")};
  font-size: ${rem("15px")};
  padding: ${rem("4px")} ${rem("10px")};
`;

const largeStyles = css`
  border-radius: ${props => getBorderRadius(props, "6px")};
  border-width: ${rem("2px")};
  font-size: ${rem("22px")};
  padding: ${rem("12px")} ${rem("26px")};
`;

const mediumStyles = css`
  border-radius: ${props => getBorderRadius(props, "6px")};
  border-width: ${rem("2px")};
  font-size: ${rem("17px")};
  padding: ${rem("14px")} ${rem("26px")};
`;
const mediumSmallStyles = css`
  border-radius: ${rem("6px")};
  border-width: ${rem("2px")};
  font-size: ${rem("13px")};
  padding: ${rem("8px")} ${rem("12px")};
`;

const smallStyles = css`
  border-radius: ${props => getBorderRadius(props, "3px")};
  border-width: ${rem("2px")};
  font-size: ${rem("14px")};
  padding: ${rem("12px")} ${rem("24px")};
`;

const smallerStyles = css`
  border-radius: ${props => getBorderRadius(props, "3px")};
  border-width: ${rem("1px")};
  font-size: ${rem("11px")};
  padding: ${rem("6px")} ${rem("16px")};
`;

const smediumStyles = css`
  border-radius: ${props => getBorderRadius(props, "6px")};
  border-width: ${rem("2px")};
  font-size: ${rem("16px")};
  padding: ${rem("14px")} ${rem("20px")};
`;

const Button = styled(
  forwardRef(
    (
      {
        borderless,
        block,
        children,
        color,
        lower,
        outline,
        pill,
        square,
        tag: Component,
        textColor,
        value,
        ...rest
      },
      ref
    ) => (
      <Component {...rest} ref={ref}>
        {children}
      </Component>
    )
  )
)`
  ${baseStyles};

  background: ${({ borderless, color, outline }) =>
    borderless || outline ? "transparent" : colors[color]};
  border-style: solid;
  border-color: ${({ borderless, color }) =>
    borderless ? "transparent" : colors[color]};
  display: ${({ block }) => (block ? "block" : "inline-block")};
  color: ${props => getTextColor(props)} !important;
  width: ${({ block }) => (block ? "100%" : "auto")};

  &:focus:not(:disabled),
  &:hover:not(:disabled) {
    background-color: ${({ borderless, color }) =>
      borderless ? "transparent" : darken(0.03, colors[color])};
    border-color: ${({ borderless, color }) =>
      borderless ? "transparent" : darken(0.03, colors[color])};
    color: ${props => getTextColor(props, "hover")} !important;
  }

  &:disabled {
    background-color: ${({ borderless, color }) =>
      borderless ? "transparent" : lighten(0.2, colors[color])};
    border-color: ${({ borderless, color }) =>
      borderless ? "transparent" : lighten(0.2, colors[color])};
    color: ${props => getTextColor(props, "disabled")} !important;
  }

  ${({ size }) => {
    switch (size) {
      case "compact":
        return compactStyles;
      case "compact-small":
        return compactSmallStyles;
      case "large":
        return largeStyles;
      case "medium-small":
        return mediumSmallStyles;
      case "smaller":
        return smallerStyles;
      case "small":
        return smallStyles;
      case "smedium":
        return smediumStyles;
      case "medium":
      default:
        return mediumStyles;
    }
  }}
  ${({ borderless }) =>
    borderless
      ? "border-left: 0; border-right: 0; padding-left: 0; padding-right: 0;"
      : ""}
`;

function getBorderRadius(props = {}, defaultRadius) {
  if (props?.pill) {
    return "50rem";
  }

  if (props?.borderless || props?.square || !defaultRadius) {
    return "0";
  }

  return rem(defaultRadius);
}

Button.propTypes = {
  borderless: bool,
  block: bool,
  className: string,
  children: node,
  disabled: bool,
  href: string,
  lower: bool,
  onClick: func,
  outline: bool,
  pill: bool,
  size: string,
  square: bool,
  tag: oneOfType([element, elementType, node, func, string]),
  textColor: string,
  to: oneOfType([func, object, string]),
  value: oneOfType([number, string]),
};

Button.defaultProps = {
  borderless: false,
  block: false,
  color: "gray600",
  disabled: false,
  lower: false,
  outline: false,
  square: false,
  tag: "button",
};

export default Button;
